import type { ProfileData } from '@/types/profile';
import { isRetailProfile, isStayProfile } from '@/lib/profile/verticalMatchers';
import { resolveRetailSalesMode } from '@/lib/retailSales/retailSalesMode';

/**
 * Whether a street address may be published, and if so what of it.
 *
 * ## Why this is one function and not three call sites
 *
 * The rule was being applied one surface at a time: the profile card hid it for
 * Stay, the storefront JSON-LD emitted it unconditionally, `llms.txt` included
 * it in the machine-readable profile, and search matched against it. Four
 * surfaces, three different answers, and a fourth added later would be written
 * by whichever person was nearest.
 *
 * ## The rule
 *
 * **Stay never publishes it.** A Stay property is somebody's home. "14 Botha
 * Avenue" tells anyone reading that the people who let it out sleep there.
 * POPIA and the tenant's own safety are both about that.
 *
 * **Retail publishes it only when the seller has a premises.** This is what the
 * physical/online/both answer is for. A seller who said "online only" ships from
 * a depot or works from home, and must not end up published at either. An
 * *unanswered* seller is treated as not publishing, because structured data is
 * cached permanently by crawlers and the cost of wrongly withholding a street
 * line is a weaker search result, while wrongly publishing one cannot be undone.
 *
 * **Everything else publishes it.** Shops, trades and restaurants have commercial
 * premises that are already publicly findable, and a guest needs an address to
 * arrive.
 */

export interface StreetAddressDecision {
  /** True when the street line may be shown. */
  publishable: boolean;
  /** The street line, or an empty string when withheld. */
  street: string;
  /** Short explanation, used in tests and admin surfaces. Never shown to buyers. */
  reason: 'stay-home' | 'retail-online-only' | 'retail-unanswered' | 'commercial';
}

const clean = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * Decide whether this profile's street address may be published.
 *
 * Takes a loosely-typed record so Firestore documents can be passed straight
 * through without a cast at every call site.
 */
export function resolveStreetAddressVisibility(
  profile: Partial<ProfileData> | null | undefined,
): StreetAddressDecision {
  const street = clean(profile?.physicalAddress);

  if (!street) {
    return { publishable: false, street: '', reason: 'commercial' };
  }

  if (profile && isStayProfile(profile as ProfileData)) {
    return { publishable: false, street: '', reason: 'stay-home' };
  }

  if (profile && isRetailProfile(profile as ProfileData)) {
    const mode = resolveRetailSalesMode(profile);
    if (mode === 'online') {
      return { publishable: false, street: '', reason: 'retail-online-only' };
    }
    if (mode === null) {
      return { publishable: false, street: '', reason: 'retail-unanswered' };
    }
    // 'physical' and 'both' both have premises a customer can be directed to.
    return { publishable: true, street, reason: 'commercial' };
  }

  return { publishable: true, street, reason: 'commercial' };
}

/** The street line, or an empty string when it must not be published. */
export function getPublishableStreetAddress(
  profile: Partial<ProfileData> | null | undefined,
): string {
  return resolveStreetAddressVisibility(profile).street;
}

/** Whether a street address may be published at all. */
export function mayPublishStreetAddress(
  profile: Partial<ProfileData> | null | undefined,
): boolean {
  return resolveStreetAddressVisibility(profile).publishable;
}